import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import "moment-timezone";
import "moment-duration-format";
import { ConversationPlayerProps } from "../../types/interface";
import styles from "./Media.module.css";
import Media from "./Media";
import KeyMoments from "./KeyMoments";
import BackButton from "../UI/BackButton";
import FlagBadge from "../Common/FlagBadge";
import MediaTypeBadge from "../Common/MediaTypeBadge";
import CxTopicBadge from "../Common/CxTopicBadge";
import OrderDetails from "./OrderDetails";
import InfoBlock2 from "../Metrics/Conversations/InfoBlock2";
import { Link, useSearchParams } from "react-router-dom";
import { getSearchParams } from "../../utils/search";
import ConversationFilters from "../Common/ConversationFilters";
import ScoreCard from "../Review/ScoreCard";
import Comments from "../Comments/Comments";
import AllReviews from "../AllReviews/AllReviews";
import Actions from "../Actions/Actions";
import { AuthContext } from "../../store/auth-context";
import { getCookie } from "../../utils/common";
import useMediaPlayer from "./useMediaPlayer";
import Ratings from "./Ratings";
import { URL } from "../../constants";
import OtherConversations from "./OtherConversations";
import SectionsScore from "./SectionsScore";
import CustomerEffortAssessment from "../CustomerEffortAssessment/CustomerEffortAssessment";

// icon
import commentIcon from "../../assets/images/comment-icon.svg";
import avgReviewIcon from "../../assets/images/average-review-icon.svg";
import actionsIcon from "../../assets/images/action-icon.svg";

const PlayerView = ({ data, id }: ConversationPlayerProps) => {
  const authCtx = useContext(AuthContext);
  const isAdmin = authCtx.userRole === "admin";
  const isQA = authCtx.userRole === "qa";
  const isManager = authCtx.userRole === "manager";

  const [searchParams] = useSearchParams();
  const searchFilters = getSearchParams(searchParams);

  const [keyMomentSelectedId, setKeyMomentSelectedId] = useState(null);

  const [rightPanelActiveComponent, setRightPanelActiveComponent] =
    useState("");
  const [isCEAActive, setIsCEAActive] = useState(false);

  const {
    curTime,
    duration,
    playing,
    isMuted,
    setPlaying,
    setClickedTime,
    setIsMuted,
  } = useMediaPlayer();

  let conversationLabeling = `x_ssense_labels_called_out_v1`;
  let cxTopic =
    data?.labelling_analysis?.[conversationLabeling][
      `x_ssense_conversationcxtopic_v1`
    ] || "";

  let totalDuration =
    data?.customer_agent_data_analysis_results?.agent_customer_data || "";

  let keyMomentsData = data[conversationLabeling]?.ss_keymomentsdata;

  // let orderId = data.type === 'AUDIO' ? conversationLabeling?.ss_ordernumbers_call : conversationLabeling?.ss_ordernumbers_chat
  // let productName = data.type === 'AUDIO' ? conversationLabeling?.ss_productnames_call : conversationLabeling?.ss_productnames_chat

  let agentName =
    data?.agent_customer_identification_service_output?.data?.agent?.name || "";
  let fileName = agentName ? `${agentName}_${data.thread_id}` : data.thread_id;

  let sentimentPrediction =
    data?.deepcat_sentiment_prediction_service_output?.data;

  // converting unix time stamp to time
  let createdAt = data?.date || "";

  let flags = {
    customerEscalation: data[`x_ssense_thread_escalated_v1`],
    durationAHT: data[`x_ssense_flag_callduration_v1`],
    excessiveHold: data[`x_ssense_flag_customerhold_v1`],
    agentSkippedIntro:
      data[`x_ssense_agent_skip_intro_flag_v1`],
    agentSkippedSalutation:
      data[`x_ssense_agent_skipped_salutation_detected_v1`],
    agentProfanity:
      data[`x_ssense_agent_profanity_detected_v1`],
    customerProfanity:
      data[`x_ssense_customer_profanity_detected_v1`],
    agentDecliningSentiment:
      data[`x_ssense_agent_declining_sentiment_v1`],
    customerDecliningSentiment:
      data[`x_ssense_customer_declining_sentiment_v1`],
    customerToneFrustrated:
      data[`x_ssense_customer_tone_frustrated_detected_v1`],
    customerToneImpolite:
      data[`x_ssense_customer_tone_impolite_detected_v1`],
    customerToneSad:
      data[`x_ssense_customer_tone_sad_detected_v1`],
    agentToneFrustrated:
      data[`x_ssense_agent_tone_frustrated_detected_v1`],
    agentToneImpolite:
      data[`x_ssense_agent_tone_impolite_detected_v1`],
  };

  let totalSentiments = {
    positiveSentiments:
      data[
        `x_ssense_customer_sentiment_overview_positive_count_v1`
      ],
    negativeSentiments:
      data[
        `x_ssense_customer_sentiment_overview_negative_count_v1`
      ],
    neutralSentiments:
      data[
        `x_ssense_customer_sentiment_overview_neutral_count_v1`
      ],
  };

  let transcript = data?.customer_agent_data_analysis_results;

  let keyMoments =
    data?.labelling_analysis?.[
      `x_ssense_labels_called_out_v1`
    ][`x_ssense_keymomentsdata_v1`];

  useEffect(() => {
    keyMomentsData &&
      keyMomentsData.length > 0 &&
      keyMomentsData.map((moment: { [key: string]: any }, i: number) => {
        moment.id = i + 1;
      });
  }, [keyMomentsData]);

  const toggleRightPanelComponent = (component: string = "") => {
    setRightPanelActiveComponent(component);
  };

  const cancelOtherConversationsHandler = () => {
    const cancelModal = document
      .getElementById("otherConversationsModal")
      ?.querySelectorAll(
        ".dismiss-otherConversationsModal"
      )[0] as HTMLButtonElement;
    cancelModal.click();
  };

  return (
    <>
      <div className={`${styles.playerViewContainer} bodyContainer`}>
        <div className={styles.pageHeaderContainer}>
          <div className="page-header container">
            <BackButton />

            <div className="align-items-baseline justify-content-between">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <h1 className="inner-page-heading me-1 mb-0" title={fileName}>
                    {fileName}
                  </h1>
                  <MediaTypeBadge type={data.communication_mode} />
                  
                </div>
                <div className="d-flex">
                  {/* Right Panel section */}
                  {/* <a href={`https://${authCtx.currentTenant}support.zendesk.com/agent/tickets/${fileName}`} target="_blank" className={`dropdown-item ${styles.getSectionsBtn} d-flex align-items-center`}>View in Zendesk</a>
                  <button
                    className={`dropdown-item ${styles.getSectionsBtn}`}
                    data-bs-toggle="modal"
                    data-bs-target="#otherConversationsModal"
                  >
                    View Conversations
                  </button> */}
                  <ul className={`${styles.rightCTA} list-unstyled d-flex`}>
                    <li
                      title="Feedback"
                      onClick={() => toggleRightPanelComponent("comments")}
                      className={`${
                        rightPanelActiveComponent === "comments"
                          ? styles.active
                          : ""
                      }`}
                    >
                      <img src={commentIcon} />
                    </li>
                    <li
                      title="Reviews"
                      onClick={() => toggleRightPanelComponent("allreviews")}
                      className={`${
                        rightPanelActiveComponent === "allreviews"
                          ? styles.active
                          : ""
                      }`}
                    >
                      <img src={avgReviewIcon} />
                    </li>
                    {/* {
                                            (isAdmin || isQA || isManager) && <li title="Actions" onClick={() => toggleRightPanelComponent('actions')} className={`${rightPanelActiveComponent === 'actions' ? styles.active : ''}`}>
                                                <img src={actionsIcon} />
                                            </li>
                                        } */}
                  </ul>
                </div>
              </div>
              <div className="align-items-center justify-content-end">
                {
                  // view === 'agent' ? // this need to be removed
                  //     <div className={`${styles.reviewedScore} ms-2`}>Score: 94%</div> : // this need to be removed
                  // (isAdmin || isQA || isManager) &&
                  // (
                  //     reviewStats?.current_user?.status == 'published' ?
                  //         <div className={`${styles.reviewedScore} ms-2`}>Score: {Math.round(reviewStats?.current_user?.score)}%</div>
                  //         :
                  //         <button
                  //             type="button"
                  //             className={`btn ms-2 ${reviewStats?.current_user?.score > 0 ? styles.btnPending : 'btn-primary'}`}
                  //             onClick={() => toggleRightPanelComponent('scorecard')}
                  //         >{reviewStats?.current_user?.score > 0 ? 'Review Pending' : 'Review'}</button>
                  // )
                }
                <p>
                  <span className={`${styles.createdAt} col-sm-4`}>
                  {moment
                      .tz(createdAt, "UTC").format("MMMM D, YYYY  h:mm A z")}
                  </span>
                </p>
                {cxTopic &&
                  (cxTopic.toLowerCase() != "none" ? (
                    <CxTopicBadge topic={cxTopic} />
                  ) : null)}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white pt-4">
          <div className="container">
            {/* Scores */}
            <div className="row justify-content-between">
              <div className="col-sm-2">
                <Ratings />
              </div>
              <SectionsScore
                setIsCEAActive={setIsCEAActive}
                isCEAActive={isCEAActive}
              />
              <div className={`col-sm-3`}>
                <InfoBlock2
                  id={data?.id}
                  setIsCEAActive={setIsCEAActive}
                  isCEAActive={isCEAActive}
                />
              </div>
            </div>
            <div className={`row ${styles.mediaContainerBlock}`}>
              <div className="col-sm-8">
                <Media
                  totalDuration={totalDuration}
                  summary={data?.call_summary?.call_summary}
                  transcript={transcript}
                  botMessageCount={
                    data[`x_ssense_bot_message_count_v1`]
                  }
                  customerMessageCount={
                    data[`x_ssense_customer_message_count_v1`]
                  }
                  agentMessageCount={
                    data[`x_ssense_agent_message_count_v1`]
                  }
                  keyMomentsData={keyMomentsData}
                  keyMomentSelectedId={keyMomentSelectedId}
                  setKeyMomentSelectedId={setKeyMomentSelectedId}
                  fileName={data.file_name}
                  filePath={data.file_path}
                  type={data.communication_mode}
                  curTime={curTime}
                  duration={duration}
                  playing={playing}
                  isMuted={isMuted}
                  setPlaying={setPlaying}
                  setClickedTime={setClickedTime}
                  setIsMuted={setIsMuted}
                  totalSentiments={totalSentiments}
                  utterances={data?.utterances}
                  keyMoments={keyMoments}
                />
              </div>
              <div className="col-sm-4">
                {isCEAActive ? (
                  <CustomerEffortAssessment id={data?.id} />
                ) : (
                  <ScoreCard id={data?.id} />
                )}

                {/* {
                                    ((orderId && orderId.length > 0) || (productName && productName.length > 0)) &&
                                    <OrderDetails 
                                        orderId={orderId}
                                        productName={productName}
                                    />
                                }                        
                                {
                                    ((data.highlights && Object.keys(data.highlights).length > 0) || (searchFilters && Object.keys(searchFilters).length > 0) ) ?
                                        <div className={`${styles.keyWordsContainer} mb-3 p-3`}>
                                            <h3>Filters</h3>
                                            <div className="d-flex flex-wrap">
                                                <ConversationFilters 
                                                    filters={data.highlights || searchFilters} 
                                                />
                                            </div>
                                        </div>
                                        :
                                        null
                                }
                                {
                                    cxTopic && keyMomentsData &&
                                    <KeyMoments
                                        keyMomentsData={keyMomentsData}
                                        keyMomentSelectedId={keyMomentSelectedId}
                                        setKeyMomentSelectedId={setKeyMomentSelectedId}
                                    />
                                }
                                 */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel Section */}

      <div
        className={styles.rightPanelContainer}
        hidden={rightPanelActiveComponent ? false : true}
      >
        <Comments
          type={data.communication_mode}
          id={data?.id}
          toggleRightPanelComponent={toggleRightPanelComponent}
          rightPanelActiveComponent={rightPanelActiveComponent}
          curTime={curTime}
          setPlaying={setPlaying}
        />
        <AllReviews
          id={data?.id}
          toggleRightPanelComponent={toggleRightPanelComponent}
          rightPanelActiveComponent={rightPanelActiveComponent}
        />
        <Actions
          toggleRightPanelComponent={toggleRightPanelComponent}
          rightPanelActiveComponent={rightPanelActiveComponent}
        />
      </div>
      <OtherConversations
        threadID={data.thread_id}
        sessionID={data.session_id}
        cancelHandler={cancelOtherConversationsHandler}
      />
    </>
  );
};

export default PlayerView;
